import { CollectionService } from 'app/collection/collection.service';
import { AfterViewInit, Component, OnInit, OnDestroy, TemplateRef } from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { NavigationEnd, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { NgbDropdownConfig, NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { EntityTypeEnum, GetEntityQuery, GetUserQuery, RoleEnum } from '../API.service';
import { NGXLogger } from 'ngx-logger';
import { GeneratorComponent } from 'app/generator/generator.component';
import { Location } from '@angular/common';
import { EntityService, GetEntityQueryExtended } from 'app/shared/entity.service';
import { BoardService } from 'app/board/board.service';
import { UtilsService } from 'app/shared/utils.service';
import { DomainFrameworksModalComponent } from 'app/shared/domain-frameworks-modal/domain-frameworks-modal.component';
import { UserService } from 'app/shared/user.service';
import { AppLevelRoleEnum } from 'app/shared/enums/appLevelRoles.enum';
import { UsersSettingsService } from 'app/users-settings/users-settings.service';
import { Subscription } from 'rxjs';
import { ModalTemplateComponent } from 'app/shared/modal-template/modal-template.component';
import { CUSTOMAPIService } from 'app/custom-api.service';
import { environment } from 'environments/environment';
import { APIDocEnum } from 'app/shared/enums/apiDoc.enum';
import { RootEntityContextService } from 'app/shared/root-entity-context.service';

interface IEntityDropdown {
  id?: string;
  name?: any;
  image?: string;
}
@Component({
  selector: 'cygov-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  providers: [NgbDropdownConfig],
  standalone: false,
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  notificationCount = 3;
  entityData: IEntityDropdown[] = [];
  selectedRootEntity: IEntityDropdown;
  isBoard = false;
  modalRef: NgbModalRef;
  user: GetUserQuery;
  entityList: GetEntityQueryExtended[] = [];
  subEntity: GetEntityQuery = null;
  complianceQualification: string;
  showEntitySummary = false;
  isAdmin = false;
  isWelcome = false;
  loading = true;
  currentRoute: string;
  isBnBCyberSite = UtilsService.isBnBCyberSite;
  isBnb = UtilsService.isBnB;
  bnbLogoPath = './assets/images/brown-brown-logo.png';
  mainLogoName = UtilsService.companyConfig.mainLogoName;
  isLogoDisabled: boolean = false;
  subscriptionList: Subscription[] = [];
  instanceConfigurationModalOptions: NgbModalOptions = {
    centered: true,
    size: 'sm',
    windowClass: 'instance-configuration-window',
    backdropClass: 'instance-configuration-backdrop',
    keyboard: false,
  };
  isVendor: boolean = false;
  ngbModalOptions: NgbModalOptions = {
    size: 'sm',
    windowClass: 'modal-template-cont',
    backdropClass: 'edit-profile-modal-backdrop modal-template-cont-backdrop md-modal-template-backdrop',
    backdrop: 'static',
  };
  closeModalInstance;
  isOtpEnabled: boolean = false;
  userTokenMetadata: { expiresInDays: number; status: string; isTokenExists: boolean; token: string } = {
    expiresInDays: 0,
    status: '',
    isTokenExists: false,
    token: '',
  };
  isTokenVisible: boolean = false;
  profileLoading: boolean = false;
  userName: string = '';
  isAPIEnabled: boolean = false;
  apiDocUrl: string = '';

  constructor(
    private authService: AuthService,
    private entityService: EntityService,
    private boardService: BoardService,
    private router: Router,
    private modalService: NgbModal,
    private _location: Location,
    private toastrService: ToastrService,
    config: NgbDropdownConfig,
    private logger: NGXLogger,
    private userService: UserService,
    private usersSettingsService: UsersSettingsService,
    private collectionService: CollectionService,
    private customApiService: CUSTOMAPIService,
    private rootEntityContextService: RootEntityContextService
  ) {
    config.placement = 'bottom-right';
  }

  ngAfterViewInit() {
    this.isBoard = this.router.url.includes('board');
    this.currentRoute = this.router.url;
  }

  async ngOnInit(): Promise<void> {
    try {
      this.subscriptionList.push(
        this.usersSettingsService.getIsAddNewUSerModalOpened().subscribe(res => {
          this.isLogoDisabled = res;
        })
      );

      this.isWelcome = this.authService.isFirstTimeUser();
      this.authService.welcomeGuideStatus.subscribe(change => {
        if (change) {
          this.isWelcome = false;
        }
      });
      this.router.events.subscribe(url => {
        if (url instanceof NavigationEnd) {
          this.currentRoute = this.router.url;
          this.isBoard = this.router.url.includes('board');
        }
      });
      this.isAdmin = this.userService.hasPermissionHierarchy(AppLevelRoleEnum.ADMIN);
      this.user = await this.userService.getCurrentUser();
      const role = this.userService.getCurrentRole();
      this.isVendor = role.name.toLowerCase() === RoleEnum.VENDOR.toLowerCase();

      if (this.isAdmin) {
        // this.entityList = await this.getRootEntityList();
        this.entityData = this.initEntityData();
        this.boardService.getCurrentEntityId().subscribe(entityId => {
          if (entityId && entityId !== 'null') {
            this.selectedRootEntity = this.entityData.find(data => data.id === entityId);
          }
        });
      }
      this.loading = false;
      this.authService.setHeaderLoaderStatus(this.loading);
    } catch (e) {
      this.logger.error(e);
      this.toastrService.error('HeaderComponent Error');
      this.loading = false;
    }
  }

  initEntityData(): IEntityDropdown[] {
    return this.entityList.map(entity => ({
      id: entity.id,
      name: entity.name,
      image: entity.logoUrl,
    }));
  }

  setEntity(): void {
    this.boardService.setCurrentEntityId(this.selectedRootEntity.id);
    this.router.navigate(['/']).then(() => this.router.navigate(this.boardService.generateUrlWithBaseUrl('wizard')));
  }

  async getRootEntityList(): Promise<GetEntityQueryExtended[]> {
    return await this.entityService.listEntitysByEntityType(EntityTypeEnum.ROOT_ENTITY, null, null, true);
  }

  signOut(): void {
    this.userService
      .logOut()
      .then(() => {
        this.toastrService.success('Logged out');
        this.entityService.isUserLoggedIn = false;
        this.entityService.flushCachedData();
        this.router.navigate(['/login']);
      })
      .catch(err => {
        this.logger.error(err);
        this.toastrService.error('Log out failed');
      });
    // nullifying object because in case of logging out it is keeping the data of previous user
    this.collectionService.singleTypeQuestionActiveUsers = {};
  }

  async goHomeByRole(): Promise<void> {
    if (!this.isLogoDisabled) {
      await this.userService.navigateByRole();
    }
  }

  navigateTo(route: string, queryString: any = null): void {
    if (queryString) {
      this.router.navigate([route], { queryParams: queryString });
    } else {
      this.router.navigate([route]);
    }
  }

  openGeneratorModal(): void {
    const modalRef = this.modalService.open(GeneratorComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
      windowClass: 'generator-modal',
      backdropClass: 'generator-backdrop',
    });

    modalRef.result.then(
      () => {},
      () => {
        this._location.back();
      }
    );
  }

  openFrameworksSettings(): void {
    const modalRef = this.modalService.open(DomainFrameworksModalComponent, this.instanceConfigurationModalOptions);
    modalRef.componentInstance.modalResult.subscribe((result: boolean) => {
      if (result) {
        modalRef.close();
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptionList.forEach(listener => listener.unsubscribe());
  }

  generateApiKey(): void {
    if (this.profileLoading) {
      return;
    }
    this.profileLoading = true;
    this.customApiService
      .generateApiKey()
      .then((res: any) => {
        if (!res) {
          this.toastrService.error('Failed to generate API Key.');
          this.profileLoading = false;
          return;
        }

        res.body
          .json()
          .then((apiResponse: any) => {
            this.postKeyGenerationResponse(apiResponse.data);
            this.toastrService.success('API Key generated successfully');
          })
          .catch(() => {
            this.profileLoading = false;
            this.toastrService.error('Failed to generate API Key.');
          });
      })
      .catch(err => {
        this.profileLoading = false;
        let errorMessage = 'Failed to generate API Key.';
        if (err?.message) {
          errorMessage = err?.message;
        }
        this.toastrService.error(errorMessage);
      });
  }

  async openProfileModal(contentRef: TemplateRef<any>, footerContentRef: TemplateRef<any>): Promise<void> {
    this.profileLoading = true;
    this.userName = this.user.name;
    const modalRef = this.modalService.open(ModalTemplateComponent, this.ngbModalOptions);
    modalRef.componentInstance.title = `EDIT ${this.user?.name?.toUpperCase()} PROFILE`;
    modalRef.componentInstance.contentRef = contentRef;
    modalRef.componentInstance.footerContentRef = footerContentRef;
    this.closeModalInstance = modalRef;
    modalRef.componentInstance.modalResult.subscribe((result: boolean) => {
      if (result) {
        this.isTokenVisible = false;
      }
    });

    // Determine if user or their role is ADMIN or MSSP
    const userRole = this.userService.currentUserRole;
    const userEntityIds = this.user?.entityIds || [];
    const roleEntityIds = userRole?.entityIds || [];
    const roleName = userRole?.name?.toLowerCase() || '';

    const isAdminOrMssp =
      roleName === AppLevelRoleEnum.ADMIN.toLowerCase() || roleName === AppLevelRoleEnum.MSSP.toLowerCase();

    const isLowerRole =
      roleName === AppLevelRoleEnum.VENDOR.toLowerCase() || roleName === AppLevelRoleEnum.PARTICIPANT.toLowerCase();

    // If neither user nor their role have any entity, and user isn't admin/mssp, disable API
    // If user is a lower role, disable API
    if (isLowerRole || (userEntityIds.length === 0 && roleEntityIds.length === 0 && !isAdminOrMssp)) {
      this.isAPIEnabled = false;
    } else {
      let entity = null;
      // If ENTITY_LEADER or has RootEntityAccess, determine entity id
      const isEntityLeader = roleName === AppLevelRoleEnum.ENTITY_LEADER.toLowerCase() || userRole?.isRootEntityAccess;
      const entityId = userEntityIds[0] || roleEntityIds[0] || null;

      if (isEntityLeader && entityId) {
        entity = await this.rootEntityContextService.getRootEntityById(entityId);
      } else {
        // Otherwise, get subentity and its parent
        const subEntityId = entityId;
        if (subEntityId) {
          entity = await this.rootEntityContextService.getSubEntity(subEntityId);
          if (entity?.parentId) {
            entity = await this.rootEntityContextService.getRootEntityById(entity.parentId);
          }
        }
      }

      // Default to enabled if entity flag is undefined, else use entity flag
      this.isAPIEnabled = entity?.isCentraleyesApiEnabled || isAdminOrMssp;
    }

    // Now check user-level API enabled if entity-level is enabled
    this.apiDocUrl = APIDocEnum[environment.name.toLowerCase()] || '';
    if (this.isAPIEnabled) {
      this.isAPIEnabled =
        this.user?.apiAccess === undefined || this.user?.apiAccess === null ? true : this.user?.apiAccess;

      const res = await this.getUserToken();
      if (res?.body) {
        const apiResponse: any = await res.body.json();
        this.userTokenMetadata.expiresInDays = this.getExpiresInDays(apiResponse.data.expiresAt);
        this.userTokenMetadata.status = apiResponse.data.apiKeyStatus;
        this.userTokenMetadata.isTokenExists = true;
        this.userTokenMetadata.token = `${apiResponse.data.token}`;
      }
    }

    this.profileLoading = false;
  }

  getExpiresInDays(expiresAt: string): number {
    const expiresAtDate = new Date(expiresAt);
    const today = new Date();
    const diffInMs = expiresAtDate.getTime() - today.getTime();
    return Math.max(0, Math.ceil(diffInMs / (1000 * 60 * 60 * 24)));
  }

  reGenerateApiKey() {
    if (this.profileLoading) {
      return;
    }
    this.profileLoading = true;
    this.customApiService
      .regenerateApiKey()
      .then((res: any) => {
        if (!res) {
          this.toastrService.error('Failed to Re-Generate API Key.');
          this.profileLoading = false;
          return;
        }

        res.body
          .json()
          .then((apiResponse: any) => {
            this.postKeyGenerationResponse(apiResponse.data);
            this.toastrService.success('API Key Re-Generated Successfully');
          })
          .catch(() => {
            this.profileLoading = false;
            this.toastrService.error('Failed to Re-Generate API Key.');
          });
      })
      .catch(err => {
        this.profileLoading = false;
        let errorMessage = 'Failed to Re-Generate API Key.';
        if (err?.message) {
          errorMessage = err?.message;
        }
        this.toastrService.error(errorMessage);
      });
  }

  postKeyGenerationResponse(apiResponse: any) {
    this.userTokenMetadata.expiresInDays = this.getExpiresInDays(apiResponse.expiresAt);
    this.userTokenMetadata.status = apiResponse.apiKeyStatus;
    this.userTokenMetadata.isTokenExists = true;
    this.userTokenMetadata.token = `${apiResponse.token}`;
    this.profileLoading = false;
  }

  async saveProfile(): Promise<void> {
    if (this.profileLoading) {
      return;
    }
    this.profileLoading = true;
    if (this.userName === this.user.name) {
      this.toastrService.success('Profile Saved Successfully');
      this.closeModalInstance.close();
    }
    const res = await this.updateUserProfile();
    if (!res) {
      this.toastrService.error('Failed to Save Profile.');
      this.profileLoading = false;
      return;
    }
    this.toastrService.success('Profile Saved Successfully');
    this.closeModalInstance.close();
    this.profileLoading = false;
  }

  closeProfileModal(): void {
    this.isTokenVisible = false;
    this.closeModalInstance.close();
  }

  async getUserToken(): Promise<any> {
    try {
      return await this.customApiService.getUserToken();
    } catch (error) {
      return null;
    }
  }

  toggleTokenVisibility(): void {
    if (this.profileLoading) {
      return;
    }
    this.isTokenVisible = !this.isTokenVisible;
  }

  async updateUserProfile(): Promise<any> {
    try {
      return await this.customApiService.UpdateUser({ id: this.user.id, name: this.user.name });
    } catch (error) {
      return null;
    }
  }
}
